import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  AnalyticOperation,
  AnalyticOperationClassData,
  Chat,
  Currency,
  CurrencyExchange,
  KeyValueItem,
  Project,
  Service,
  ServiceOrder,
  ServiceOrderTemplateCreate,
} from '@app/shared';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  deepClean,
  DocumentService,
  FlowEventService,
  LoadUnitService,
  NavigationService,
  ProjectService,
  setGetParamsById,
  StServiceService,
} from '@app/core';
import { AuthService } from '../../../core/services/auth.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalValuesService } from '../../../core/services/global-values.service';

type OrderDetail = { isInvoiceOrder: boolean; orderId: number } | undefined;

@Injectable({
  providedIn: 'root',
})
export class ProjectUtilsService implements OnDestroy {
  public projects: Project[] = [];
  currenciesExchange: CurrencyExchange[] = [];
  currencies: Currency[] = [];
  /** Variables de cache **/
  cachedProjects = new Map<number, Project>();
  cachedServices = new Map<number, Service>();

  /** BehaviorSubject para comunicar cuando se crean órdenes **/
  ordersCreated$ = new BehaviorSubject<boolean>(false);

  /** Método para notificar que se crearon órdenes **/
  notifyOrdersCreated(): void {
    console.log('DEBUG: notifyOrdersCreated called');
    this.ordersCreated$.next(true);
    // Reset after a short delay to allow re-triggering
    setTimeout(() => this.ordersCreated$.next(false), 100);
  }
  closeAllDrawers$ = new BehaviorSubject<boolean>(false);
  navigateToServiceOrders$ = new BehaviorSubject<boolean>(false);
  currentDetailChat$ = new BehaviorSubject<Chat | undefined>(undefined);
  currentChatEntetyName$ = new BehaviorSubject<string>('');
  uncompletedService: Service | undefined;
  uncompletedProject: Project | undefined;
  currentKeyValueItemEditing$: BehaviorSubject<KeyValueItem | null> =
    new BehaviorSubject<KeyValueItem | null>(null);
  private destroy$: any;

  constructor(
    private projectService: ProjectService,
    private stServiceService: StServiceService,
    private documentService: DocumentService,
    private flowEventService: FlowEventService,
    private authService: AuthService,
    private router: Router,
    private navigationService: NavigationService,
    private loadUnitService: LoadUnitService,
    private globalValuesService: GlobalValuesService
  ) {
    this.globalValuesService.currenciesExchange$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.currenciesExchange = value));
    this.globalValuesService.currencies$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.currencies = value));
  }

  private _openDetailOrderDrawer = new BehaviorSubject<OrderDetail>(undefined);

  get openDetailOrderDrawer(): Observable<
    undefined | { isInvoiceOrder: boolean; orderId: number }
  > {
    return this._openDetailOrderDrawer as Observable<
      undefined | { isInvoiceOrder: boolean; orderId: number }
    >;
  }

  set openDetailOrderDrawer(
    value: undefined | { isInvoiceOrder: boolean; orderId: number }
  ) {
    this._openDetailOrderDrawer.next(value);
  }

  private _selectedProject: Project | undefined;

  get selectedProject(): Project | undefined {
    return this._selectedProject;
  }

  set selectedProject(project: Project | undefined) {
    if (project) this.cachedProjects.set(project.id, project);
    this._selectedProject = project;
  }

  _selectedService: Service | undefined;

  get selectedService(): Service | undefined {
    return this._selectedService;
  }

  set selectedService(service: Service | undefined) {
    //console.log('se actualizo el servicio', service);
    if (service) this.cachedServices.set(service.id, service);
    this._selectedService = service;
  }

  set currentKeyValueItemEditing(value: KeyValueItem | null) {
    this.currentKeyValueItemEditing$.next(value);
  }

  getCurrentKeyValueItemEditing() {
    return this.currentKeyValueItemEditing$ as Observable<KeyValueItem | null>;
  }

  processServiceOrderForMoneyData(serviceOrder: ServiceOrder): {
    planData: AnalyticOperationClassData;
    executedData: AnalyticOperationClassData;
    engagedData: AnalyticOperationClassData;
    totalData: AnalyticOperationClassData;
  } {
    const categories: any = { Planned: [], Executed: [], Engaged: [] };
    serviceOrder.analyticOperations?.forEach((ao: AnalyticOperation) =>
      categories[ao.class].push(ao)
    );

    const planData: AnalyticOperationClassData =
      this.calculateAnalyticOpertationData(categories.Planned);
    const executedData: AnalyticOperationClassData =
      this.calculateAnalyticOpertationData(categories.Executed);
    const engagedData: AnalyticOperationClassData =
      this.calculateAnalyticOpertationData(categories.Engaged);

    const incomeTotal =
      planData.incomeTotal + executedData.incomeTotal + engagedData.incomeTotal;
    const totalMargin =
      planData.margin + executedData.margin + engagedData.margin;
    let totalData: AnalyticOperationClassData = {
      incomeTotal: incomeTotal,
      expensesTotal:
        planData.expensesTotal +
        executedData.expensesTotal +
        engagedData.expensesTotal,
      margin: totalMargin,
      percent: 0,
    };

    totalData.percent =
      totalData.margin < 0
        ? totalData.expensesTotal !== 0
          ? (totalData.margin / totalData.expensesTotal) * -100
          : 0
        : totalData.incomeTotal !== 0
        ? (totalData.margin / incomeTotal) * 100
        : 0;

    return {
      planData,
      executedData,
      engagedData,
      totalData,
    };
  }

  calculateAnalyticOpertationData(
    analyticOperations: AnalyticOperation[]
  ): AnalyticOperationClassData {
    let incomeTotal: number = 0;
    let expensesTotal: number = 0;
    let margin: number = 0;
    let percent: number = 0;

    const idUsd = this.currencies.find((item) => item.isoCode === 'USD')?.id;
    const usdExchange =
      this.currenciesExchange?.find((item) => item.currencyId === idUsd)
        ?.baseRate || 1;

    if (analyticOperations.length > 0) {
      analyticOperations.forEach((ao) => {
        let amountUsd: number;
        if (ao.currency?.id === idUsd) {
          amountUsd = ao.amount;
        } else {
          amountUsd =
            (ao.amount * usdExchange) /
            (ao.currency?.currentCurrencyExchange?.baseRate ?? 1);
        }

        if (ao.type === 'Ingreso') {
          incomeTotal += amountUsd;
        } else {
          expensesTotal -= amountUsd;
        }
      });

      // Se suma porque desde arriba el valor de expensesTotal es negativo
      margin = incomeTotal + expensesTotal;
      percent =
        margin < 0
          ? expensesTotal !== 0
            ? (margin / expensesTotal) * -100
            : 0
          : incomeTotal !== 0
          ? (margin / incomeTotal) * 100
          : 0;
    }
    // if (analyticOperations.length > 0) {
    //   const amountRequests = analyticOperations.map(ao =>
    //     this.analyticOperationService.calculateAmount(ao.quantity, ao.type)
    //   );
    //
    //   forkJoin(amountRequests).pipe(takeUntil(this.destroy$)).subscribe({
    //     next: (amounts) => {
    //       analyticOperations.forEach((ao, index) => {
    //         const amount = amounts[index];
    //         if (ao.type === 'Ingreso') {
    //           incomeTotal += amount;
    //         } else {
    //           expensesTotal -= amount;
    //         }
    //       });
    //
    //       margin = incomeTotal + expensesTotal;
    //       percent = incomeTotal !== 0 ? (margin / incomeTotal) * 100 : -100;
    //
    //       // Actualizar UI o estado aquí
    //     },
    //     error: (err) => console.error("Error:", err)
    //   });
    // }

    return {
      incomeTotal,
      expensesTotal,
      margin,
      percent,
    };
  }

  /** Extrae el proyecto de la respuesta API (soporta rows, data directo o project como objeto) */
  private extractProjectFromResponse(rsp: any): Project | undefined {
    if (!rsp?.project) return undefined;
    const p = rsp.project;
    const d = p.data;
    if (d?.rows && Array.isArray(d.rows) && d.rows.length > 0) {
      return d.rows[0];
    }
    if (d && typeof d === 'object' && d.id != null) {
      return d;
    }
    if (p && typeof p === 'object' && p.id != null) {
      return p;
    }
    return undefined;
  }

  getProjectAndServiceData(): Observable<boolean> {
    // Se obtienen los ids del proyecto y del servicio de la URL
    const segments = window.location.pathname
      .split('/')
      .filter((s) => s !== '');
    let projectId: number;
    let serviceId: number | undefined;

    // ✅ Validar que segments tenga elementos suficientes
    if (!segments || segments.length === 0) {
      console.error('No URL segments found');
      return of(false);
    }

    // Buscar el formato: /projects/detail/{projectId}/service/{serviceId}
    const detailIndex = segments.indexOf('detail');
    if (detailIndex !== -1) {
      // ✅ Verificar que hay un elemento después de 'detail'
      if (segments.length <= detailIndex + 1) {
        console.error('Invalid URL format: detail found but no projectId');
        return of(false);
      }
      projectId = Number(segments[detailIndex + 1]);
      const serviceIndex = segments.indexOf('service');
      if (serviceIndex !== -1) {
        // ✅ Verificar que hay un elemento después de 'service'
        if (segments.length <= serviceIndex + 1) {
          console.error('Invalid URL format: service found but no serviceId');
          return of(false);
        }
        serviceId = Number(segments[serviceIndex + 1]);
      }
    } else {
      // Buscar el formato: /projects/{projectId}/services/{serviceId}
      const projectsIndex = segments.indexOf('projects');
      if (projectsIndex !== -1 && segments.length > projectsIndex + 1) {
        projectId = Number(segments[projectsIndex + 1]);
        // Buscar 'services' (case insensitive)
        const servicesIndex = segments.findIndex(
          (s) => s.toLowerCase() === 'services'
        );
        if (servicesIndex !== -1 && segments.length > servicesIndex + 1) {
          serviceId = Number(segments[servicesIndex + 1]);
        }
      } else {
        // Si no se encuentra ningún formato válido, retornar false
        console.error('No valid URL format found. Segments:', segments);
        return of(false);
      }
    }

    // Validar que projectId sea un número válido
    if (isNaN(projectId) || projectId <= 0) {
      console.error('Invalid projectId:', projectId);
      return of(false);
    }

    // Validar que serviceId sea un número válido si está presente
    if (serviceId !== undefined && (isNaN(serviceId) || serviceId <= 0)) {
      console.error('Invalid serviceId:', serviceId);
      return of(false);
    }

    // Se obtienen los datos cacheados del proyecto y del servicio si existen
    const cachedProject: Project | undefined =
      this.cachedProjects.get(projectId);
    const cachedService: Service | undefined = serviceId
      ? this.cachedServices.get(serviceId)
      : undefined;

    // Si los datos cacheados existen se procesa la info y se actualiza el estado global
    // (Se actualizan los datos cacheados sin el Chat)
    if (!serviceId && cachedProject) {
      if (!this.checkPermises(cachedProject)) {
        return of(false);
      }
      this.setBreadcrumb(cachedProject);
      this.selectedProject = cachedProject;

      return of(true);
    } else if (serviceId && cachedProject && cachedService) {
      if (!this.checkPermises(cachedProject, cachedService)) {
        return of(false);
      }
      this.setBreadcrumb(cachedProject, cachedService);
      this.selectedProject = cachedProject;
      this.selectedService = cachedService;

      return of(true);
    }

    // Si no existen datos cacheados se obtienen los datos de la API
    // Se configuran los requests del Project
    // Si hay serviceId, el proyecto puede ser opcional, así que capturamos errores
    let requests: {
      [key: string]: Observable<any>; // Firma de índice para permitir propiedades dinámicas
      project: Observable<any>;
      containers: Observable<any>;
    } = {
      project: this.projectService.getProject(setGetParamsById(projectId)).pipe(
        catchError((err: any) => {
          // Manejar errores del interceptor (statusText: 'Error in response body') o errores HTTP normales
          const errorStatusText =
            err?.statusText || err?.error?.statusText || '';
          const hasErrorInBody =
            errorStatusText === 'Error in response body' ||
            err?.error?.statusCode;

          if (hasErrorInBody) {
            console.warn(
              'Interceptor detected error in project response body:',
              err?.error || err
            );
          } else {
            console.warn('Error fetching project:', err);
          }

          // Si hay serviceId, permitir continuar sin proyecto
          if (serviceId) {
            return of({ data: { rows: [] } }); // Retornar estructura vacía para no romper forkJoin
          }
          // Si no hay serviceId, también retornar vacío para evitar que forkJoin falle
          return of({ data: { rows: [] } });
        })
      ),
      containers: this.projectService
        .getProjectContainer({
          startRow: 0,
          endRow: 10000,
          sortProperty: 'id',
          sortDirection: 'asc',
          filterProperty: 'projectId',
          filterValue: projectId.toString(),
        })
        .pipe(
          catchError((err: any) => {
            // Manejar errores del interceptor o errores HTTP normales
            const errorStatusText =
              err?.statusText || err?.error?.statusText || '';
            const hasErrorInBody =
              errorStatusText === 'Error in response body' ||
              err?.error?.statusCode;

            if (hasErrorInBody) {
              console.warn(
                'Interceptor detected error in containers response body:',
                err?.error || err
              );
            } else {
              console.warn('Error fetching containers:', err);
            }
            // Retornar estructura vacía para no romper forkJoin
            return of({ data: { rows: [] } });
          })
        ),
    };

    // Si estamos en ServiceDetail tambien se configura para obtener los datos del servicio
    if (serviceId) {
      requests['service'] = this.stServiceService
        .getServiceById(serviceId)
        .pipe(
          catchError((err: any) => {
            // Manejar errores del interceptor o errores HTTP normales
            const errorStatusText =
              err?.statusText || err?.error?.statusText || '';
            const hasErrorInBody =
              errorStatusText === 'Error in response body' ||
              err?.error?.statusCode;

            if (hasErrorInBody) {
              console.error(
                'Interceptor detected error in service response body:',
                err?.error || err
              );
            } else {
              console.error('Error fetching service:', err);
            }
            // Si el servicio falla, retornar estructura vacía (luego verificaremos si está vacío)
            return of({ data: { rows: [] } });
          })
        );
      requests['serviceDocuments'] = this.documentService
        .getDocument({
          startRow: 0,
          endRow: 100,
          sortDirection: 'asc',
          sortProperty: 'id',
          entityId: serviceId,
          entityType: 'Service',
        })
        .pipe(
          catchError((err: any) => {
            // Manejar errores del interceptor o errores HTTP normales
            const errorStatusText =
              err?.statusText || err?.error?.statusText || '';
            const hasErrorInBody =
              errorStatusText === 'Error in response body' ||
              err?.error?.statusCode;

            if (hasErrorInBody) {
              console.warn(
                'Interceptor detected error in service documents response body:',
                err?.error || err
              );
            } else {
              console.warn('Error fetching service documents:', err);
            }
            return of({ data: { rows: [] } });
          })
        );
      requests['flow'] = this.flowEventService
        .getFlow({
          startRow: 0,
          endRow: 100,
          sortDirection: 'asc',
          sortProperty: 'id',
          filterProperty: 'serviceId',
          filterValue: serviceId.toString(),
        })
        .pipe(
          catchError((err: any) => {
            // Manejar errores del interceptor o errores HTTP normales
            const errorStatusText =
              err?.statusText || err?.error?.statusText || '';
            const hasErrorInBody =
              errorStatusText === 'Error in response body' ||
              err?.error?.statusCode;

            if (hasErrorInBody) {
              console.warn(
                'Interceptor detected error in flow response body (optional):',
                err?.error || err
              );
            } else {
              console.warn('Error fetching flow (optional):', err);
            }
            return of({ data: { rows: [] } }); // Flow es opcional
          })
        );
      requests['template'] = this.stServiceService
        .getServiceOrderTemplate({
          startRow: 0,
          endRow: 1,
          sortDirection: 'asc',
          sortProperty: 'id',
          filterProperty: 'serviceId',
          filterValue: serviceId.toString(),
        })
        .pipe(
          catchError((err: any) => {
            // Manejar errores del interceptor o errores HTTP normales
            const errorStatusText =
              err?.statusText || err?.error?.statusText || '';
            const hasErrorInBody =
              errorStatusText === 'Error in response body' ||
              err?.error?.statusCode;

            if (hasErrorInBody) {
              console.warn(
                'Interceptor detected error in template response body (optional):',
                err?.error || err
              );
            } else {
              console.warn('Error fetching template (optional):', err);
            }
            return of({ data: { rows: [] } }); // Template es opcional
          })
        );
    } else {
      // Si no hay Service se hace la llamada para obtener los projectDocuments pq la API de Documents no admite peticiones concurrentes
      requests['projectDocuments'] = this.documentService
        .getDocument({
          startRow: 0,
          endRow: 100,
          sortDirection: 'asc',
          sortProperty: 'id',
          entityId: projectId,
          entityType: 'Project',
        })
        .pipe(
          catchError((err: any) => {
            // Manejar errores del interceptor o errores HTTP normales
            const errorStatusText =
              err?.statusText || err?.error?.statusText || '';
            const hasErrorInBody =
              errorStatusText === 'Error in response body' ||
              err?.error?.statusCode;

            if (hasErrorInBody) {
              console.warn(
                'Interceptor detected error in project documents response body:',
                err?.error || err
              );
            } else {
              console.warn('Error fetching project documents:', err);
            }
            return of({ data: { rows: [] } }); // Retornar estructura vacía
          })
        );
    }

    // Se hacen las peticiones que se hayan configurado
    return forkJoin(requests).pipe(
      switchMap((rsp: any) => {
        // Si estamos en ServiceDetail, intentar obtener el servicio aunque no haya proyecto
        if (serviceId) {
          // Primero verificar si tenemos el servicio
          if (!rsp.service?.data?.rows || rsp.service.data.rows.length === 0) {
            console.warn(
              'No service data found in forkJoin response, attempting to fetch service directly...'
            );
            // Si no hay servicio en la respuesta del forkJoin, intentar obtenerlo directamente
            // Esto puede ocurrir si hubo un error en la petición pero fue capturado
            return this.stServiceService.getServiceById(serviceId).pipe(
              switchMap((serviceRsp: any) => {
                if (serviceRsp?.data) {
                  // Si conseguimos el servicio directamente, continuar
                  const service: Service = serviceRsp.data;
                  service.flow = undefined; // Flow puede no estar disponible
                  service.serviceOrderTemplate = undefined;
                  service.documents = [];

                  // Intentar obtener proyecto si el servicio tiene projectId
                  let project: Project | undefined;
                  if (service.projectId) {
                    return this.projectService
                      .getProject(setGetParamsById(service.projectId))
                      .pipe(
                        switchMap((projectRsp: any) => {
                          if (
                            projectRsp?.data?.rows &&
                            projectRsp.data.rows.length > 0
                          ) {
                            project = projectRsp.data.rows[0];
                            if (project) {
                              project.projectContainers = [];
                              project.documents = [];
                            }
                          }
                          return of({
                            project,
                            service,
                            rsp: { service: { data: serviceRsp.data } },
                          });
                        }),
                        catchError(() => {
                          // Continuar sin proyecto
                          return of({
                            project: undefined,
                            service,
                            rsp: { service: { data: serviceRsp.data } },
                          });
                        })
                      );
                  }

                  return of({
                    project: undefined,
                    service,
                    rsp: { service: { data: serviceRsp.data } },
                  });
                } else {
                  // Si no podemos obtener el servicio, no podemos continuar
                  console.error('Could not fetch service directly either');
                  return of({
                    check: false,
                    project: undefined,
                    service: undefined,
                    rsp: undefined,
                  });
                }
              }),
              catchError((directServiceErr: any) => {
                console.error(
                  'Error fetching service directly:',
                  directServiceErr
                );
                // Si no podemos obtener el servicio de ninguna manera, no podemos continuar
                return of({
                  check: false,
                  project: undefined,
                  service: undefined,
                  rsp: undefined,
                });
              })
            );
          }

          const service: Service = rsp.service.data.rows[0];
          // El flow puede ser opcional, no redirigir si no existe
          service.flow =
            rsp.flow?.data &&
            rsp.flow.data.rows &&
            rsp.flow.data.rows.length > 0
              ? rsp.flow.data.rows[0]
              : undefined;
          service.serviceOrderTemplate = rsp.template?.data?.rows?.[0];
          service.documents = rsp.serviceDocuments?.data?.rows || [];

          // Función modificada para crear templates si es necesario
          const createTemplatesIfNeeded = (): Observable<void> => {
            let loadUnitTemplate: any;
            if (!rsp.template?.data?.rows?.[0]) {
              return this.loadUnitService
                .createLoadUnitTemplate({ packageTypeId: 1 })
                .pipe(
                  switchMap((luTRsp) => {
                    loadUnitTemplate = luTRsp.data;
                    const soTemplateParams: ServiceOrderTemplateCreate = {
                      loadUnitTemplateId: luTRsp.data.id,
                      createdAt: new Date().toISOString(),
                      serviceId: service.id,
                      isActive: true,
                      differentiatedBlock: false,
                      originContactId: service.originContactId,
                      originId: service.originId,
                      destinationContactId: service.destinationContactId,
                      destinationId: service.destinationId,
                    };
                    return this.stServiceService.createServiceOrderTemplate(
                      deepClean(soTemplateParams)
                    );
                  }),
                  tap((soTRsp) => {
                    service.serviceOrderTemplate = soTRsp.data;
                    if (service.serviceOrderTemplate)
                      service.serviceOrderTemplate.loadUnitTemplate =
                        loadUnitTemplate;
                  }),
                  map(() => undefined) // Emite señal de completitud
                );
            }
            return of(undefined); // No necesita crear template
          };

          // Verificar si tenemos el proyecto
          let project: Project | undefined;
          if (rsp.project?.data?.rows && rsp.project.data.rows.length > 0) {
            project = rsp.project.data.rows[0];
            if (project) {
              project.projectContainers = rsp.containers?.data?.rows || [];
              project.documents = undefined; // Los documentos están en el servicio
            }
            // Si tenemos proyecto, continuar normalmente con la creación de templates
            return createTemplatesIfNeeded().pipe(
              map(() => ({ project, service, rsp }))
            );
          } else {
            console.warn(
              'No project data found in response, but continuing with service'
            );
            // Si no hay proyecto pero sí servicio, intentar obtenerlo si el servicio tiene projectId
            if (service.projectId) {
              // Intentar obtener el proyecto si el servicio tiene projectId
              return this.projectService
                .getProject(setGetParamsById(service.projectId))
                .pipe(
                  switchMap((projectRsp: any) => {
                    if (
                      projectRsp?.data?.rows &&
                      projectRsp.data.rows.length > 0
                    ) {
                      project = projectRsp.data.rows[0];
                      if (project) {
                        project.projectContainers = [];
                        project.documents = undefined;
                      }
                    } else {
                      console.warn(
                        'Could not fetch project from service.projectId, continuing with service only'
                      );
                    }
                    // Continuar con la creación de templates y retorno
                    return createTemplatesIfNeeded().pipe(
                      map(() => ({ project, service, rsp }))
                    );
                  }),
                  catchError((err) => {
                    console.warn(
                      'Error fetching project, continuing with service only:',
                      err
                    );
                    // Continuar sin proyecto - el servicio es suficiente
                    return createTemplatesIfNeeded().pipe(
                      map(() => ({ project: undefined, service, rsp }))
                    );
                  })
                );
            } else {
              // Si el servicio no tiene projectId, continuar sin proyecto
              console.warn(
                'Service has no projectId, continuing without project'
              );
              return createTemplatesIfNeeded().pipe(
                map(() => ({ project: undefined, service, rsp }))
              );
            }
          }
        }

        // Si no estamos en ServiceDetail, necesitamos el proyecto
        let project = this.extractProjectFromResponse(rsp);
        if (!project) {
          // Fallback: intentar con getProjectByCompanies (p.ej. proyectos sin macro tareas)
          console.warn('No project in standard format, trying getProjectByCompanies...');
          return this.projectService
            .getProjectByCompanies(setGetParamsById(projectId))
            .pipe(
              switchMap((fallbackRsp: any) => {
                project =
                  fallbackRsp?.data?.rows?.[0] ??
                  (fallbackRsp?.data && typeof fallbackRsp.data === 'object' && fallbackRsp.data.id != null
                    ? fallbackRsp.data
                    : undefined);
                if (!project) {
                  console.error('No project data found in fallback response');
                  return of({
                    check: false,
                    project: undefined,
                    service: undefined,
                    rsp: undefined,
                  });
                }
                project.projectContainers = rsp.containers?.data?.rows || [];
                project.documents = rsp.projectDocuments?.data?.rows || [];
                if (!project.services) project.services = [];
                return of({ project, service: undefined, rsp });
              }),
              catchError((err) => {
                console.error('Fallback getProjectByCompanies failed:', err);
                return of({
                  check: false,
                  project: undefined,
                  service: undefined,
                  rsp: undefined,
                });
              })
            );
        }

        project.projectContainers = rsp.containers?.data?.rows || [];
        project.documents = rsp.projectDocuments?.data?.rows || [];
        if (!project.services) project.services = [];

        return of({ project, service: undefined, rsp });
      }),
      switchMap(({ project, service, rsp }) => {
        // Si estamos en ServiceDetail, necesitamos al menos el servicio
        if (serviceId) {
          if (!service) {
            console.error(
              '❌ CAUSA 1: No hay servicio cuando serviceId está presente'
            );
            return of({ check: false, project: undefined, service: undefined });
          }

          console.log('✅ Servicio encontrado:', {
            serviceId: service.id,
            serviceName: service.name,
            hasProject: !!project,
            projectId: project?.id,
          });

          // Si tenemos proyecto, revisar permisos con ambos
          if (project) {
            const hasPermissions = this.checkPermises(project, service);
            console.log(
              '🔐 Verificación de permisos con proyecto:',
              hasPermissions
            );
            if (!hasPermissions) {
              console.error(
                '❌ CAUSA 2: Sin permisos para ver el proyecto/servicio'
              );
              return of({ check: false, project, service });
            }
            this.setBreadcrumb(project, service);
          } else {
            // Si no hay proyecto pero sí servicio, continuar sin proyecto
            // El servicio es suficiente para mostrar la vista de detalles
            if (service.projectId) {
              console.warn(
                '⚠️ Service tiene projectId pero proyecto no fue cargado, continuando solo con servicio'
              );
            } else {
              console.warn(
                '⚠️ Service no tiene projectId, continuando sin proyecto'
              );
            }
            // No configuramos breadcrumb si no hay proyecto para evitar errores
            // El servicio por sí solo es suficiente para continuar
          }

          console.log('✅ Retornando check: true para continuar con servicio');
          return of({ check: true, project, service });
        }

        // Si no hay proyecto, retornar false (solo si no estamos en ServiceDetail)
        if (!project) {
          return of({ check: false, project: undefined, service: undefined });
        }

        // Revisar permisos
        if (!this.checkPermises(project)) {
          return of({ check: false, project, service: undefined });
        }

        // Configurar breadcrumb
        this.setBreadcrumb(project);

        return of({ check: true, project, service: undefined });
      }),
      tap((result) => {
        // ✅ Proteger el tap para manejar casos donde result puede ser undefined
        const { project, service, check } = result || {};
        // Si estamos en ServiceDetail, necesitamos al menos el servicio
        if (serviceId) {
          if (service && check) {
            // Si tenemos proyecto, asignarlo también
            if (project) {
              this.selectedProject = project;
            }
            this.selectedService = service; // Service con template actualizado si se creó
          }
        } else {
          // Si no estamos en ServiceDetail, necesitamos el proyecto
          if (project && check) {
            this.selectedProject = project;
            this.selectedService = undefined;
          }
        }
      }),
      map((result) => {
        // ✅ Proteger el map para manejar casos donde result puede ser undefined
        return result?.check ?? false;
      }),
      catchError((e: any) => {
        console.error('Error in getProjectAndServiceData:', e);
        // ✅ Manejar específicamente errores del interceptor con statusText 'Error in response body'
        const errorStatusText = e?.statusText || e?.error?.statusText || '';
        const hasErrorInBody =
          errorStatusText === 'Error in response body' || e?.error?.statusCode;

        if (hasErrorInBody) {
          console.warn(
            'Interceptor detected error in response body at forkJoin level:',
            e?.error || e
          );
          console.warn(
            'This error should have been caught by individual request catchError handlers'
          );
        }

        // ✅ Si hay serviceId, intentar obtener el servicio directamente como último recurso
        if (serviceId) {
          console.warn(
            'forkJoin failed, attempting to fetch service directly as fallback...'
          );
          return this.stServiceService.getServiceById(serviceId).pipe(
            switchMap((serviceRsp: any) => {
              if (serviceRsp?.data) {
                // Si conseguimos el servicio directamente, asignarlo y retornar true
                const service: Service = serviceRsp.data;
                service.flow = undefined;
                service.serviceOrderTemplate = undefined;
                service.documents = [];

                // Asignar el servicio
                this.selectedService = service;

                // Intentar obtener proyecto si existe
                if (service.projectId) {
                  return this.projectService
                    .getProject(setGetParamsById(service.projectId))
                    .pipe(
                      switchMap((projectRsp: any) => {
                        if (
                          projectRsp?.data?.rows &&
                          projectRsp.data.rows.length > 0
                        ) {
                          const project = projectRsp.data.rows[0];
                          if (project) {
                            project.projectContainers = [];
                            project.documents = [];
                            // Verificar permisos antes de asignar
                            if (this.checkPermises(project, service)) {
                              this.selectedProject = project;
                              this.setBreadcrumb(project, service);
                            }
                          }
                        }
                        return of(true); // Retornar true porque tenemos el servicio
                      }),
                      catchError(() => {
                        // Continuar sin proyecto - el servicio es suficiente
                        return of(true);
                      })
                    );
                }

                return of(true); // Retornar true porque tenemos el servicio
              } else {
                console.error('Could not fetch service directly as fallback');
                return of(false);
              }
            }),
            catchError((fallbackErr: any) => {
              console.error(
                'Error fetching service directly as fallback:',
                fallbackErr
              );
              return of(false);
            })
          );
        }

        // ✅ Si no hay serviceId, retornar false
        return of(false);
      })
    );
  }

  checkPermises(project: Project, service?: Service): boolean {
    const user = this.authService.loggedUser$.value;
    if (!service) {
      if (
        !user?.viewUnassignedProjectsAndServices &&
        !project.services?.some((s) => s.supplierId === user?.partner.id)
      ) {
        return false;
      }
    } else {
      if (
        !user?.viewUnassignedProjectsAndServices &&
        service.supplierId !== user?.partner.id
      ) {
        return false;
      }
    }
    return true;
  }

  setBreadcrumb(project: Project, service?: Service) {
    if (!service) {
      this.navigationService.setBreadcrumb([
        { label: 'Proyectos', navigate: '/projects' },
        { label: project.name },
      ]);
    } else {
      this.navigationService.setBreadcrumb([
        { label: 'Proyectos', navigate: '/projects' },
        {
          label: project.name,
          navigate: '/projects/detail/' + service!.projectId,
        },
        { label: service!.name },
      ]);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
